import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import MarketingSiteLayout from '../../marketing/MarketingSiteLayout'
import { PUBLIC_INQUIRY_PATH } from '../../marketing/marketingNav'
import { ENABLE_BILLING_COUPONS } from '../../config/featureFlags'
import { api } from '../../lib/api'
import {
  billingParamsFromCheckoutProduct,
  checkoutProducts,
  checkoutTierGroups,
  formatKrw,
  organizationContractPlan,
} from '../../content/publicPlans'

function resolveProductIdFromQuery(raw) {
  if (!raw) return checkoutProducts[0]?.id || ''
  return checkoutProducts.some((p) => p.id === raw) ? raw : checkoutProducts[0]?.id || ''
}

function OrganizationContractCheckoutCard({ plan }) {
  if (!plan) return null
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="checkout-organization-contract">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 id="checkout-organization-contract" className="text-sm font-bold text-slate-900">{plan.name}</h3>
          <p className="mt-1 text-xs font-medium text-slate-500">{plan.period}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{plan.badge}</span>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <div>
          <div className="text-2xl font-black tracking-tight text-slate-900">{plan.price}</div>
          <p className="mt-2 text-xs leading-relaxed text-slate-600">{plan.desc}</p>
        </div>
        <Link
          to={plan.cta.to}
          className="inline-flex min-h-[2.75rem] items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800 hover:border-sky-300 hover:bg-sky-50"
        >
          {plan.cta.label}
        </Link>
      </div>
    </section>
  )
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams()
  const [selectedId, setSelectedId] = useState(() => resolveProductIdFromQuery(searchParams.get('product')))

  useEffect(() => {
    const next = resolveProductIdFromQuery(searchParams.get('product'))
    setSelectedId(next)
  }, [searchParams])
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeRefund, setAgreeRefund] = useState(false)
  const [couponCode, setCouponCode] = useState(() => (searchParams.get('coupon') || '').trim())
  const [serverQuote, setServerQuote] = useState(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState('')

  useEffect(() => {
    const c = searchParams.get('coupon')
    if (c != null) setCouponCode(String(c).trim())
  }, [searchParams])

  const selected = useMemo(
    () => checkoutProducts.find((p) => p.id === selectedId) || checkoutProducts[0],
    [selectedId]
  )

  const paymentAmountKrw = useMemo(() => Number(selected?.supplyPriceKrw) || 0, [selected])

  useEffect(() => {
    setServerQuote(null)
    setQuoteError('')
  }, [selectedId])

  const fetchCheckoutQuote = useCallback(async () => {
    setQuoteError('')
    setQuoteLoading(true)
    try {
      const { planCode, period } = billingParamsFromCheckoutProduct(selected)
      const res = await api.post('/billing/public/checkout-quote', {
        planCode,
        period,
        couponCode: couponCode.trim() || undefined,
      })
      const data = res?.data?.data ?? null
      setServerQuote(data)
    } catch {
      setServerQuote(null)
      setQuoteError('견적을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
    } finally {
      setQuoteLoading(false)
    }
  }, [couponCode, selected])

  const loginReturnSearch = useMemo(() => {
    const p = new URLSearchParams()
    if (selected?.id) p.set('checkoutProduct', selected.id)
    if (ENABLE_BILLING_COUPONS && couponCode.trim()) p.set('prefillCoupon', couponCode.trim())
    const s = p.toString()
    return s ? `?${s}` : ''
  }, [couponCode, selected?.id])

  const allAgreed = agreeTerms && agreePrivacy && agreeRefund

  return (
    <MarketingSiteLayout>
      <div className="mx-auto max-w-5xl px-4 py-12 md:py-16">
        <p className="text-xs font-semibold text-brand-sky">구매·결제</p>
        <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">요금제와 결제</h1>
        <div className="mt-4 max-w-2xl space-y-3 text-sm leading-relaxed text-slate-600">
          <p>
            <Link to="/pricing" className="font-semibold text-brand-navy underline-offset-2 hover:underline">
              요금 안내
            </Link>
            를 통해 플랜별 제공 서비스의 차이를 확인 할 수 있습니다. 
          </p>
          <p>
            GA·조직 도입은 <strong className="text-slate-800">단체계약</strong>으로 상담합니다. 개요는{' '}
            <Link to="/pricing" className="font-semibold text-brand-navy underline-offset-2 hover:underline">
              요금 안내
            </Link>
            를 참고하고, 상담은{' '}
            <Link to={PUBLIC_INQUIRY_PATH} className="font-semibold text-brand-navy underline-offset-2 hover:underline">
              도입 문의
            </Link>
            로 연락해 주세요.
          </p>
          <p>
            결제는 로그인이 필수이므로, 회원 가입 후 결제를 진행해주세요.
          </p>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_22rem]">
          <div>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <h2 className="text-base font-bold text-slate-900">요금제·기간 선택</h2>
              <Link to="/pricing" className="text-xs font-semibold text-brand-navy underline-offset-2 hover:underline">
                요금 안내·플랜 비교 →
              </Link>
            </div>

            <div className="mt-4 space-y-6">
              {checkoutTierGroups.map((tier) => (
                <section
                  key={tier.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm sm:p-5"
                  aria-labelledby={`checkout-tier-${tier.id}`}
                >
                  <h3 id={`checkout-tier-${tier.id}`} className="text-sm font-bold text-slate-900">
                    {tier.title}
                    {tier.badge ? (
                      <span className="ml-2 text-xs font-medium text-slate-500">· {tier.badge}</span>
                    ) : null}
                  </h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {tier.products.map((p) => {
                      const active = selected?.id === p.id
                      const amount = Number(p.supplyPriceKrw) || 0
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedId(p.id)}
                          className={`flex w-full flex-col rounded-xl border px-4 py-3.5 text-left transition ${
                            active ? 'border-sky-400 bg-sky-50/90 ring-2 ring-sky-200' : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">
                              {p.periodLabel === '1개월' ? '1개월 이용' : '12개월 선결제'}
                            </span>
                            {p.discountPercent ? (
                              <span className="rounded-full bg-emerald-600/15 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                                할인 약 {p.discountPercent}%
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-[11px] leading-snug text-slate-600">{p.summary}</p>
                          <div className="mt-3 border-t border-slate-200/80 pt-2">
                            {p.listPriceKrw != null && Number(p.listPriceKrw) > amount ? (
                              <div className="text-sm font-medium text-slate-400 line-through">{formatKrw(p.listPriceKrw)}</div>
                            ) : null}
                            <div className="text-xl font-black tracking-tight text-slate-900">{formatKrw(amount)}</div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </section>
              ))}
              <OrganizationContractCheckoutCard plan={organizationContractPlan} />
            </div>
          </div>

          <aside className="lg:sticky lg:top-6 h-fit space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold text-slate-900">주문 요약</h2>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4 text-slate-600">
                  <dt>선택</dt>
                  <dd className="text-right font-medium text-slate-900">{selected?.name ?? '-'}</dd>
                </div>
                <div className="flex justify-between gap-4 text-slate-600">
                  <dt>이용 기간</dt>
                  <dd className="text-right text-slate-900">{selected?.periodLabel ?? '-'}</dd>
                </div>
                <div className="flex justify-between gap-4 border-t border-slate-200 pt-3 text-slate-600">
                  <dt>요금 안내 금액</dt>
                  <dd className="text-right font-medium text-slate-900">
                    {selected?.listPriceKrw != null && Number(selected.listPriceKrw) > paymentAmountKrw ? (
                      <div className="text-xs font-semibold text-slate-400 line-through">{formatKrw(selected.listPriceKrw)}</div>
                    ) : null}
                    <div>{formatKrw(paymentAmountKrw)}</div>
                  </dd>
                </div>
                {serverQuote ? (
                  <>
                    {serverQuote.coupon?.applied && typeof serverQuote.coupon.discountAmount === 'number' ? (
                      <div className="flex justify-between gap-4 text-sm text-emerald-800">
                        <dt>쿠폰 할인</dt>
                        <dd className="text-right font-semibold">−{formatKrw(serverQuote.coupon.discountAmount)}</dd>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-black text-slate-900">
                      <dt>결제 예정 금액</dt>
                      <dd className="text-right">{formatKrw(Number(serverQuote.finalPrice) || 0)}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
              <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                요금 안내 금액은 마케팅 페이지와 동일한 기준입니다.
                {serverQuote ? (
                  <>
                    {' '}
                    <strong className="text-slate-700">결제 예정 금액</strong>은 서버에 등록된 요금표와 쿠폰(가능한 경우)을 반영한 견적이며, 로그인 후
                    조직 계약·개인별 한도에 따라 달라질 수 있습니다.
                  </>
                ) : (
                  <>
                    {ENABLE_BILLING_COUPONS
                      ? ' 쿠폰이 있으면 아래에서 적용을 눌러 서버 기준 결제 예정 금액을 확인할 수 있습니다.'
                      : null}
                  </>
                )}
              </p>
            </div>

            {ENABLE_BILLING_COUPONS ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">쿠폰 코드</h2>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                  코드를 입력한 뒤 <strong className="text-slate-800">적용</strong>을 누르면 오른쪽 주문 요약에 서버 기준{' '}
                  <strong className="text-slate-800">결제 예정 금액</strong>이 표시됩니다. 쿠폰 없이 적용만 누르면 할인 없이 동일 기준으로 견적만
                  확인할 수 있습니다.
                </p>
                <label className="mt-3 block text-xs font-semibold text-slate-600" htmlFor="checkout-coupon-code">
                  쿠폰 코드
                </label>
                <input
                  id="checkout-coupon-code"
                  type="text"
                  autoComplete="off"
                  placeholder="예: SAVE50"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:border-sky-400 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => fetchCheckoutQuote()}
                  disabled={quoteLoading}
                  className="mt-3 w-full rounded-lg bg-sky-600 px-3 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {quoteLoading ? '견적 확인 중…' : '적용 · 결제 예정 금액 확인'}
                </button>
                {quoteError ? <p className="mt-2 text-[11px] text-red-600">{quoteError}</p> : null}
                {serverQuote?.coupon?.reason && !serverQuote?.coupon?.applied ? (
                  <p className="mt-2 text-[11px] leading-relaxed text-amber-800">{serverQuote.coupon.reason}</p>
                ) : null}
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  로그인 후 구독·결제 단계에서는 쿠폰 사용 가능 여부를 계정 기준으로 다시 확인합니다. 공개 페이지에서 선택한 플랜·기간은 로그인
                  화면으로 함께 전달됩니다.
                </p>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold text-slate-900">결제 수단</h2>
              <p className="mt-2 text-xs text-slate-500">PG 연동 후 카드·이체·간편결제 등을 순차적으로 지원할 예정입니다.</p>
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                준비 중 (연동 후 안내)
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold text-slate-900">필수 동의</h2>
              <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <span>
                  <Link to="/terms" className="font-semibold text-brand-navy underline-offset-2 hover:underline">
                    이용약관
                  </Link>
                  에 동의합니다.
                </span>
              </label>
              <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                />
                <span>
                  <Link to="/privacy" className="font-semibold text-brand-navy underline-offset-2 hover:underline">
                    개인정보처리방침
                  </Link>
                  에 동의합니다.
                </span>
              </label>
              <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  checked={agreeRefund}
                  onChange={(e) => setAgreeRefund(e.target.checked)}
                />
                <span>
                  <Link to="/refund-policy" className="font-semibold text-brand-navy underline-offset-2 hover:underline">
                    청약철회·환불 정책
                  </Link>
                  을 확인하였고 이에 동의합니다.
                </span>
              </label>

              <Link
                to="/login"
                state={{ from: { pathname: '/billing/subscribe', search: loginReturnSearch, hash: '' } }}
                tabIndex={allAgreed ? 0 : -1}
                className={`mt-6 flex min-h-[48px] w-full items-center justify-center rounded-full text-sm font-bold transition ${
                  allAgreed
                    ? 'bg-brand-navy text-white shadow-md hover:bg-sky-700'
                    : 'pointer-events-none cursor-not-allowed bg-slate-200 text-slate-500'
                }`}
                aria-disabled={!allAgreed}
                onClick={(e) => {
                  if (!allAgreed) e.preventDefault()
                }}
              >
                로그인하고 계속하기
              </Link>
              {!allAgreed ? <p className="mt-2 text-center text-[11px] text-slate-500">필수 동의를 모두 선택하면 진행할 수 있습니다.</p> : null}
            </div>

            <p className="text-center text-[11px] leading-relaxed text-slate-500">
              비회원은{' '}
              <Link className="font-semibold text-brand-navy underline" to={PUBLIC_INQUIRY_PATH}>
                문의하기
              </Link>
              를 이용해 주세요.
            </p>
          </aside>
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-sm text-slate-600">
          <h2 className="font-bold text-slate-900">진행 순서</h2>
          <ul className="mt-3 list-decimal space-y-2 pl-5">
            <li>
              금액·기간은{' '}
              <Link className="font-semibold text-brand-navy underline" to="/pricing">
                요금 안내
              </Link>
              와 같습니다. 쿠폰이 있으면 공개 페이지에서 적용 후 <strong className="text-slate-800">결제 예정 금액</strong>을 확인할 수 있고, 로그인
              후에는 동일 플랜·기간으로 주문이 이어지며 계정·조직 조건에 따라 금액이 달라질 수 있습니다.
            </li>
            <li>로그인 후 서비스 안의 구독·결제 메뉴에서 주문을 이어갑니다. 운영 정책에 따라 메뉴 이름·노출이 달라질 수 있습니다.</li>
            <li>
              환불·취소는{' '}
              <Link className="font-semibold text-brand-navy underline" to="/refund-policy">
                청약철회·환불 정책
              </Link>
              을 참고해 주세요.
            </li>
          </ul>
        </div>
      </div>
    </MarketingSiteLayout>
  )
}
